import axios from 'axios'

const API_BASE = process.env.REACT_APP_NEXTERA_API

/** Payload from ``run_hexagon_project_sync`` returned on job success */
/** Partial fields while job status is ``running`` (poll hexagon-sync-status) */
export interface HexagonSyncProgressSummary {
  phase?: string
  project_name?: string
  project_public_id?: string
  uploaded_images?: number
  current_floor?: string
  current_date?: string
  pending_uploads_on_date?: number
}

export interface HexagonSyncRunSummary {
  project_name?: string
  project_public_id?: string
  project_uid?: string
  mapping_source?: string
  start_date?: string | null
  end_date?: string | null
  sync_closest_vp_match?: boolean
  idempotent_date_range?: boolean
  last_synced_on?: string
  uploaded_images?: number
  uploaded_via_direct_vp?: number
  uploaded_via_closest_vp?: number
  skipped_images_no_point?: number
  skipped_already_synced?: number
  dates_skipped_no_pending_work?: number
  floor_upload_counts_by_floor?: Record<
    string,
    { uploaded: number; attempted: number }
  >
  db_viewpoint_grid_point_count_by_floor?: Record<string, number>
  synced_dates?: Array<{ floor_code: string; date: string }>
}

export interface HexagonSyncJobStatusResponse {
  job_id: string
  status: 'queued' | 'running' | 'success' | 'failed'
  summary?:
    | HexagonSyncRunSummary
    | HexagonSyncProgressSummary
    | Record<string, unknown>
  errors?: string[]
  warnings?: string[]
}

export interface HexagonSyncPreviewDateCounts {
  date: string
  photo_count: number
  would_upload_count: number
  would_skip_no_point_count: number
  would_skip_missing_image_url_count: number
}

export interface HexagonSyncPreviewFloor {
  floor_code: string
  shoot_type_uid: string
  last_synced_date?: string | null
  dates: HexagonSyncPreviewDateCounts[]
  /** Distinct (x,y) grid keys in DB for this floor */
  db_grid_point_count: number
  /** Photos in the preview window that would upload (direct or closest VP, depending on toggle) */
  matched_viewpoint_photos: number
}

/** Preview rows for photos that would not upload in the current configuration */
/** One would-upload preview thumbnail; click opens Didge floor/point when public app URL is configured */
export interface HexagonSyncPreviewImageItem {
  image_url: string
  floor_code: string
  matched_sub_viewpoint_id: number
}

export interface HexagonUnmatchedViewpointRow {
  floor_code: string
  date: string
  photo_number: string
  photo_id?: string | null
  grid_x: number
  grid_y: number
  has_image_url: boolean
  /** Nearest DB grid cell within search radius, e.g. "12,34 #567" */
  closest_vp_match?: string | null
}

export interface HexagonSyncPreviewResponse {
  project_name: string
  project_public_id: string
  project_uid: string
  mapping_source: 'env_json' | 'inferred'
  dry_run: true
  floors: HexagonSyncPreviewFloor[]
  totals: {
    candidate_photos: number
    would_upload: number
    would_upload_direct_vp: number
    would_upload_closest_vp: number
    would_skip_no_point: number
    would_skip_missing_image_url: number
    /** Exact grid miss but at least one DB point within nearby_viewpoint_match.max_chebyshev */
    skip_no_point_has_nearby_db_point: number
    /** Would upload but cache says this Hexagon photo is already stored for this viewpoint */
    would_skip_already_synced?: number
  }
  preview_images: HexagonSyncPreviewImageItem[]
  preview_images_pagination: {
    page: number
    images_per_page: number
    total_images: number
    total_pages: number
    has_prev: boolean
    has_next: boolean
  }
  start_date?: string
  end_date?: string
  generated_on: string
  /** Same flag passed to preview; mirrors sync job option */
  sync_closest_vp_match: boolean
  /** When true, sync/preview use the full date window (not watermark-filtered) and skip duplicate photo+VP uploads */
  idempotent_date_range?: boolean
  unmatched_viewpoint_rows: HexagonUnmatchedViewpointRow[]
  nearby_viewpoint_match?: {
    max_chebyshev: number
    limit_per_photo: number
  }
}

export const startHexagonSyncJob = async (params?: {
  projectName?: string
  didgeProjectId?: string
  startDate?: string
  endDate?: string
  syncClosestVpMatch?: boolean
}): Promise<HexagonSyncJobStatusResponse> => {
  const body: Record<string, string | boolean> = {}
  if (params?.projectName) {
    body.project_name = params.projectName
  }
  if (params?.didgeProjectId) {
    body.didge_project_id = params.didgeProjectId
  }
  if (params?.startDate) {
    body.start_date = params.startDate
  }
  if (params?.endDate) {
    body.end_date = params.endDate
  }
  if (params?.syncClosestVpMatch === true) {
    body.sync_closest_vp_match = true
  }
  const response = await axios.post(`${API_BASE}/users/hexagon-sync`, body)
  return response.data.data ?? response.data
}

export const getHexagonSyncStatus = async (
  jobId: string,
): Promise<HexagonSyncJobStatusResponse> => {
  const response = await axios.get(
    `${API_BASE}/users/hexagon-sync-status/${jobId}`,
  )
  return response.data.data ?? response.data
}

export const previewHexagonSync = async (params?: {
  projectName?: string
  didgeProjectId?: string
  maxPhotosPerDate?: number
  previewPage?: number
  imagesPerPage?: number
  startDate?: string
  endDate?: string
  syncClosestVpMatch?: boolean
}): Promise<HexagonSyncPreviewResponse> => {
  const body: Record<string, string | number | boolean> = {}
  if (params?.projectName) {
    body.project_name = params.projectName
  }
  if (params?.didgeProjectId) {
    body.didge_project_id = params.didgeProjectId
  }
  if (params?.maxPhotosPerDate) {
    body.max_photos_per_date = params.maxPhotosPerDate
  }
  if (params?.previewPage) {
    body.preview_page = params.previewPage
  }
  if (params?.imagesPerPage) {
    body.images_per_page = params.imagesPerPage
  }
  if (params?.startDate) {
    body.start_date = params.startDate
  }
  if (params?.endDate) {
    body.end_date = params.endDate
  }
  if (params?.syncClosestVpMatch === true) {
    body.sync_closest_vp_match = true
  }
  const response = await axios.post(
    `${API_BASE}/users/hexagon-sync-preview`,
    body,
  )
  return response.data.data ?? response.data
}
